/**
 * Posture Score — Prompt.md §17.
 *
 * Không có `CameraPlacement` trong file này, và cũng không được có: điểm số là phần "phán xét",
 * dùng chung cho mọi mode. Placement đã biến mất ở `metrics.ts`.
 */

import { scoreBands, type PostureConfig, type ScoreWeights } from "@studystraight/shared-config";
import type {
  IssueSeverity,
  PostureMetricKey,
  PostureMetrics,
  PostureStatus,
} from "@studystraight/shared-types";

/** Ngưỡng của một metric, đã quy về cùng đơn vị với `excessOf()`. */
export interface MetricThresholds {
  warning: number;
  bad: number;
}

/**
 * `distanceRatio` là tỉ lệ (1.0 = đúng baseline) còn các metric khác là delta góc (0 = đúng
 * baseline). Hai hàm dưới đây quy cả hai về một trục "vượt bao nhiêu so với baseline", nhờ đó
 * `deltaToScore()` và bảng severity chỉ cần một đường code duy nhất.
 */
export function thresholdsFor(key: PostureMetricKey, config: PostureConfig): MetricThresholds {
  switch (key) {
    case "headAngle":
      return { warning: config.headDown.warningDelta, bad: config.headDown.badDelta };
    case "torsoAngle":
      return { warning: config.leaningForward.warningDelta, bad: config.leaningForward.badDelta };
    case "shoulderTilt":
      return { warning: config.shoulderTilt.warningDelta, bad: config.shoulderTilt.badDelta };
    case "distanceRatio":
      return { warning: config.tooClose.warningRatio - 1, bad: config.tooClose.badRatio - 1 };
  }
}

export function excessOf(key: PostureMetricKey, metrics: PostureMetrics): number {
  const metric = metrics[key];
  return key === "distanceRatio" ? metric.value - 1 : metric.value;
}

export function severityOf(excess: number, thresholds: MetricThresholds): IssueSeverity {
  if (!Number.isFinite(excess)) return "NONE";
  if (excess >= thresholds.bad) return "BAD";
  if (excess >= thresholds.warning) return "WARNING";
  return "NONE";
}

/**
 * 100 khi delta ≤ 0; 75 tại `warning`; 40 tại `bad`; 0 khi delta ≥ 2·bad.
 *
 * Các mốc neo đúng vào `scoreBands` (§17): 75 là ranh giới GOOD, nên "vừa chạm ngưỡng warning"
 * phải rơi chính xác vào ranh giới đó — nếu hai bảng lệch nhau thì một tư thế bị gắn nhãn
 * WARNING vẫn có thể mang status GOOD, và người dùng thấy hai thông tin trái ngược trên cùng
 * một màn hình.
 */
export function deltaToScore(delta: number, warning: number, bad: number): number {
  if (delta <= 0) return 100;
  if (delta <= warning) return 100 - (25 * delta) / warning;
  if (delta <= bad) return 75 - (35 * (delta - warning)) / (bad - warning);
  return Math.max(0, 40 - (40 * (delta - bad)) / bad);
}

export interface ScoreResult {
  /** 0..100. */
  score: number;
  /** Metric bị loại khỏi công thức (không quan sát được ở frame này hoặc ở placement này). */
  excludedMetrics: PostureMetricKey[];
}

/**
 * Trung bình có trọng số, **tái phân bổ trọng số** trên các metric còn đo được (§17).
 *
 * Không thay metric thiếu bằng 0 điểm (sẽ báo động sai suốt phiên vì bàn học che hông), cũng
 * không thay bằng 100 điểm (sẽ thổi phồng điểm và làm báo cáo cho phụ huynh mất giá trị). Trọng
 * số của metric thiếu được chia lại cho các metric còn lại theo đúng tỉ lệ ban đầu.
 */
export function computePostureScore(
  metrics: PostureMetrics,
  config: PostureConfig,
  weights: ScoreWeights,
): ScoreResult {
  const keys = Object.keys(metrics) as PostureMetricKey[];
  const excludedMetrics: PostureMetricKey[] = [];
  let weighted = 0;
  let totalWeight = 0;

  for (const key of keys) {
    const weight = weights[key];
    const metric = metrics[key];
    if (!metric.available || !Number.isFinite(metric.value) || weight <= 0) {
      excludedMetrics.push(key);
      continue;
    }
    const { warning, bad } = thresholdsFor(key, config);
    weighted += weight * deltaToScore(excessOf(key, metrics), warning, bad);
    totalWeight += weight;
  }

  // Không còn metric nào: trả 100 để KHÔNG tự sinh cảnh báo từ hư không. Frame kiểu này phải bị
  // loại khỏi thống kê phiên bởi tầng tracking (§15) — `excludedMetrics` đủ 4 chiều là dấu hiệu.
  return { score: totalWeight === 0 ? 100 : weighted / totalWeight, excludedMetrics };
}

/** §17 — status suy ra từ score, không phải một luật song song. */
export function statusFromScore(score: number): PostureStatus {
  if (score >= scoreBands.good) return "GOOD";
  if (score >= scoreBands.needsAdjustment) return "NEEDS_ADJUSTMENT";
  return "BAD";
}
